import { useEffect } from 'react';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { getStockWarnings as getPosStockWarningsForSale } from '../../../sales/public-api'
import type { PosSaleLineInput, PosStockSalePolicy } from '../../domain/posTypes';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';

/**
 * Verifica stock del carrito en vivo (carrito lateral y modal Cobrar).
 */
export const usePosStockWarningsForCheckout = (
  currentBranchId: string | null | undefined,
  saleItems: PosSaleLineInput[],
  /** Re-dispara el cálculo al cambiar la política (sesión / sucursal). */
  stockSalePolicy: PosStockSalePolicy
) => {
  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const setStockWarningResult = usePosCheckoutStore((s) => s.setStockWarningResult);
  const setStockWarningsLoading = usePosCheckoutStore((s) => s.setStockWarningsLoading);
  const clearStockWarnings = usePosCheckoutStore((s) => s.clearStockWarnings);

  useEffect(() => {
    if (!inventoryStockUxEnabled) {
      clearStockWarnings();
      return;
    }
    if (!currentBranchId || saleItems.length === 0) {
      clearStockWarnings();
      return;
    }

    let cancelled = false;
    setStockWarningsLoading(true);
    getPosStockWarningsForSale(currentBranchId, saleItems)
      .then(({ canProceed, warnings, details }) => {
        if (!cancelled) {
          setStockWarningResult(warnings, details, canProceed);
        }
      })
      .catch((error) => {
        captureCatch('pos', 'checkout-stock-warnings')(error);
        if (!cancelled) {
          const message = 'No pudimos verificar el stock. Reintentá antes de cobrar.';
          setStockWarningResult(
            stockSalePolicy === 'block' ? [message] : [],
            [],
            stockSalePolicy !== 'block',
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStockWarningsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    inventoryStockUxEnabled,
    currentBranchId,
    saleItems,
    stockSalePolicy,
    setStockWarningResult,
    setStockWarningsLoading,
    clearStockWarnings,
  ]);
};
